import { useState, useEffect } from "react";
import {
  sendOTP as sendOTPRequest,
  verifyOTP as verifyOTPRequest,
  resetPassword as resetPasswordRequest
} from "../services/api";
import { useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");

  const [step, setStep] = useState(1);
  const [bg, setBg] = useState("");

  const [loadingOTP, setLoadingOTP] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [msg, setMsg] = useState("");

  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [strength, setStrength] = useState("");

  // 🌗 Dynamic Background
  useEffect(() => {
    const h = new Date().getHours();

    if (h >= 6 && h < 12) setBg("linear-gradient(135deg, #fceabb, #f8b500)");
    else if (h < 17) setBg("linear-gradient(135deg, #e3f2fd, #90caf9)");
    else if (h < 20) setBg("linear-gradient(135deg, #fbc2eb, #a6c1ee)");
    else setBg("linear-gradient(135deg, #2c3e50, #4ca1af)");
  }, []);

  // 🔒 Strength checker
  useEffect(() => {
    let score = 0;
    if (password.length >= 6) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[@$!%*?&]/.test(password)) score++;

    if (score <= 1) setStrength("weak");
    else if (score <= 3) setStrength("medium");
    else setStrength("strong");
  }, [password]);

  // 🔁 SEND OTP
  const sendOTP = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) return setMsg("Enter email");

    setLoadingOTP(true);
    try {
      await sendOTPRequest({ email: normalizedEmail });
      setStep(2);
      setMsg("OTP sent");
    } catch {
      setMsg("Email is not registered");
    }
    setLoadingOTP(false);
  };

  // 🔐 VERIFY OTP
  useEffect(() => {
    const verify = async () => {
      const normalizedEmail = email.trim();
      const normalizedOtp = otp.trim();

      if (normalizedEmail && normalizedOtp.length === 6) {
        setVerifying(true);

        try {
          await verifyOTPRequest({ email: normalizedEmail, otp: normalizedOtp });
          setStep(3);
        } catch {
          setMsg("Invalid OTP");
        }

        setVerifying(false);
      }
    };
    verify();
  }, [otp, email]);

  // 🔁 RESET PASSWORD + REDIRECT
  const resetPassword = async () => {
    if (password !== confirmPass) {
      return setMsg("Passwords do not match");
    }

    setResetLoading(true);

    try {
      await resetPasswordRequest({
        email: email.trim(),
        otp: otp.trim(),
        newPassword: password
      });

      setMsg("Password reset successful");

      // 🔥 REDIRECT AFTER SUCCESS
      setTimeout(() => {
        nav("/");
      }, 1500);

    } catch {
      setMsg("Reset failed");
    }

    setResetLoading(false);
  };

  return (
    <>
      <style>{`
        body { margin:0; font-family:Arial; }

        .container {
          height:100vh;
          display:flex;
          justify-content:center;
          align-items:center;
        }

        .card {
          background:rgba(255,255,255,0.2);
          backdrop-filter:blur(10px);
          padding:25px;
          border-radius:10px;
          width:300px;
        }

        input {
          width:100%;
          padding:10px;
          margin:8px 0;
          border-radius:6px;
          border:none;
        }

        .input-group {
          position:relative;
        }

        .eye {
          position:absolute;
          right:10px;
          top:50%;
          transform:translateY(-50%);
          cursor:pointer;
          font-size:14px;
        }

        button {
          width:100%;
          padding:10px;
          background:#007bff;
          color:white;
          border:none;
          border-radius:6px;
          margin-top:10px;
        }

        .bar {
          height:5px;
          border-radius:5px;
          margin-top:5px;
        }

        .weak { background:red; width:30%; }
        .medium { background:orange; width:60%; }
        .strong { background:green; width:100%; }

        .msg { text-align:center; margin-top:10px; }
      `}</style>

      <div className="container" style={{ background: bg }}>
        <div className="card">
          <h3>Reset Password</h3>

          {step === 1 && (
            <>
              <input placeholder="Email" onChange={e => setEmail(e.target.value)} />
              <button onClick={sendOTP}>
                {loadingOTP ? "Sending..." : "Send OTP"}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <input placeholder="OTP" onChange={e => setOtp(e.target.value)} />
              <div>{verifying && "Verifying..."}</div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="input-group">
                <input
                  type={showPass ? "text" : "password"}
                  placeholder="New Password"
                  onChange={e => setPassword(e.target.value)}
                />
                <span className="eye" onClick={() => setShowPass(!showPass)}>
                  👁
                </span>
              </div>

              {password && <div className={`bar ${strength}`}></div>}

              <div className="input-group">
                <input
                  type={showConfirm ? "text" : "password"}
                  placeholder="Confirm Password"
                  onChange={e => setConfirmPass(e.target.value)}
                />
                <span className="eye" onClick={() => setShowConfirm(!showConfirm)}>
                  👁
                </span>
              </div>

              <button onClick={resetPassword}>
                {resetLoading ? "Processing..." : "Reset Password"}
              </button>
            </>
          )}

          {msg && <div className="msg">{msg}</div>}
        </div>
      </div>
    </>
  );
}